import React, { useState, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { getProgramReportById } from "../../utils/api/endpoints/program-reports/program-reports";
import PDFEditor from "../../pages/automated-report/PDFEditor";
import LoadingSpinner from "../retests/utils/LoadingSpinner";
import ErrorPage from "../../pages/ErrorPage";

/**
 * Route guard component that only allows Business users to view pentest reports
 * when the report status is in the allowed list for Business.
 */
const BusinessPentestReportGuard: React.FC = () => {
    const { report_id } = useParams<{ report_id: string }>();
    const [report, setReport] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<any>(null);

    const BUSINESS_ALLOWED_STATUSES = [
        "approved",
        "business_review",
        "business_requested_changes",
        "changes_added",
        "report_updated",
        "business_approved"
    ];

    useEffect(() => {
        const fetchReport = async () => {
            if (!report_id) return;
            try {
                setIsLoading(true);
                const response = await getProgramReportById(report_id);
                // API returns { status: "success", data: { ...reportData } }
                // Extract actual report data from response.data
                const reportData = response?.data ?? response;
                setReport(reportData);
            } catch (err) {
                console.error("Error fetching pentest report for Business guard:", err);
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReport();
    }, [report_id]);

    // Show loading spinner while fetching report data
    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Show error page if report fetch failed or report not found
    if (error || !report) {
        return <ErrorPage />;
    }

    // Check if Business user has permission to view this report
    // Backend also enforces this, but this provides a better UX
    const currentStatus = report.status || "draft";
    const canView = BUSINESS_ALLOWED_STATUSES.includes(currentStatus);

    // If Business doesn't have permission, redirect to the dashboard
    if (!canView) {
        return <Navigate to="/dashboard/pentest-reports" replace />;
    }

    // If all checks pass, render the PDFEditor
    return <PDFEditor />;
};

export default BusinessPentestReportGuard;
